import { skipToken } from '@reduxjs/toolkit/query'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import penningLogo from '../../../../assets/penning.png'
import {
  ROUNDING_DIGITS_CRYPTO,
  ROUNDING_DIGITS_FIAT,
} from '../../../../constants'
import { COMPANY_INVOICE_LEGAL_LINE } from '../../../../constants/company'
import {
  useOtacGetLegacySwapInvoiceDetailsQuery,
  useOtacGetSwapInvoiceDetailsQuery,
} from '../../../../redux/services/coreApi'
import { setOtacToken } from '../../../../redux/slices/sessionSlice'
import { formatNumber, formatUtcDate } from '../../../../utils/formatters'

type SwapInvoiceProps = {}

const Item: React.FC<{ title: string; value: string }> = ({ title, value }) => {
  return (
    <div className="pr-2">
      <span>{title}</span>
      <span className="text-brand-light-text-default"> {value}</span>
    </div>
  )
}

const SwapInvoice: React.FC<SwapInvoiceProps> = () => {
  const [swapId, setSwapId] = useState<string | undefined>(undefined)
  const [isLegacy, setIsLegacy] = useState<boolean | undefined>(undefined)

  // Use conditional hooks based on legacy flag
  const { data: swapInvoiceDetails, isLoading: swapInvoiceIsLoading } =
    useOtacGetSwapInvoiceDetailsQuery(
      !isLegacy && swapId ? swapId : skipToken,
      { skip: isLegacy || !swapId }
    )

  const {
    data: legacySwapInvoiceDetails,
    isLoading: legacySwapInvoiceIsLoading,
  } = useOtacGetLegacySwapInvoiceDetailsQuery(
    isLegacy && swapId ? swapId : skipToken,
    { skip: !isLegacy || !swapId }
  )

  // Use the appropriate data source
  const finalSwapInvoiceDetails = isLegacy
    ? legacySwapInvoiceDetails
    : swapInvoiceDetails
  const finalIsLoading = isLegacy
    ? legacySwapInvoiceIsLoading
    : swapInvoiceIsLoading

  const dispatch = useDispatch()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    const otacToken = searchParams.get('otacToken')
    if (otacToken) dispatch(setOtacToken({ otacToken }))

    const swapId = searchParams.get('id')
    if (swapId) setSwapId(swapId)

    const legacy = searchParams.get('legacy')
    setIsLegacy(legacy === 'true')
  }, [])

  if (finalIsLoading) {
    return <div>Loading...</div>
  }

  const depositTotal =
    finalSwapInvoiceDetails?.swap.deposit?.amount! *
    finalSwapInvoiceDetails?.swap.deposit?.currencyToEurRate!

  const withdrawTotal =
    finalSwapInvoiceDetails?.swap.withdraw?.amount! *
    finalSwapInvoiceDetails?.swap.withdraw?.currencyToEurRate!

  const totalFee =
    finalSwapInvoiceDetails?.swap.baseFee! +
    finalSwapInvoiceDetails?.swap.bankFee!

  function splitByCapitalLetters(input?: string): string {
    if (!input) return ''
    return input
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
  }

  const network =
    finalSwapInvoiceDetails?.swap.withdraw?.toWallet?.network.name ??
    finalSwapInvoiceDetails?.swap.deposit?.toWallet?.network.name

  const wallet =
    finalSwapInvoiceDetails?.swap.withdraw?.toWallet?.address ??
    finalSwapInvoiceDetails?.swap.deposit?.toWallet?.address

  const cryptoTxnId =
    finalSwapInvoiceDetails?.swap.withdraw?.currency.currencyType ===
    'CryptoCurrency'
      ? finalSwapInvoiceDetails?.swap.withdraw?.transactionHash
      : (finalSwapInvoiceDetails?.swap.deposit as any)?.transactionHash

  const iban =
    finalSwapInvoiceDetails?.swap.deposit?.toBankAccount?.bankAccountDetails
      .iban ??
    finalSwapInvoiceDetails?.swap.withdraw?.toBankAccount?.bankAccountDetails
      .iban
  const bic =
    finalSwapInvoiceDetails?.swap.deposit?.toBankAccount?.bankAccountDetails
      .bic ??
    finalSwapInvoiceDetails?.swap.withdraw?.toBankAccount?.bankAccountDetails
      .bic

  const accountNo =
    finalSwapInvoiceDetails?.swap.deposit?.toBankAccount?.bankAccountDetails
      .accountNumber ??
    finalSwapInvoiceDetails?.swap.withdraw?.toBankAccount?.bankAccountDetails
      .accountNumber

  const regNo =
    finalSwapInvoiceDetails?.swap.deposit?.toBankAccount?.bankAccountDetails
      .registration ??
    finalSwapInvoiceDetails?.swap.withdraw?.toBankAccount?.bankAccountDetails
      .registration

  // Prefer user address when available
  const userAddress = finalSwapInvoiceDetails?.user.address ?? ''

  const withdrawInCrypto =
    finalSwapInvoiceDetails?.swap.withdraw?.currency.currencyType ===
    'CryptoCurrency'

  const roundingDigitsDepositUnitValue =
    finalSwapInvoiceDetails?.swap.deposit?.currency.currencyType ===
    'CryptoCurrency'
      ? ROUNDING_DIGITS_CRYPTO
      : ROUNDING_DIGITS_FIAT

  const roundingDigitsWithdrawUnitValue =
    finalSwapInvoiceDetails?.swap.withdraw?.currency.currencyType ===
    'CryptoCurrency'
      ? ROUNDING_DIGITS_CRYPTO
      : ROUNDING_DIGITS_FIAT

  return (
    // data-invoice-ready signals the PDF renderer (InvoicePdfService) that the
    // invoice data is on screen — it must only be "true" once data is present
    <div
      data-invoice-ready={finalSwapInvoiceDetails ? 'true' : undefined}
      className="h-screen p-5 text-[10px] font-medium bg-white text-brand-light-text-description relative"
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="font-semi text-xs mb-1 text-brand-light-text-default">
            {finalSwapInvoiceDetails?.user.name}
          </p>

          {finalSwapInvoiceDetails?.user.role === 'Organization' && (
            <p>{finalSwapInvoiceDetails?.user.vatNumber}</p>
          )}
          <p>{finalSwapInvoiceDetails?.user.email}</p>
          {userAddress && <p>{userAddress}</p>}
        </div>
        <img
          src={penningLogo}
          alt="Penning logo"
          className="h-16 w-16 rounded-full"
        />
      </div>

      <div className="grid grid-cols-2 mt-10">
        <Item
          title="Date:"
          value={`${formatUtcDate(finalSwapInvoiceDetails?.swap.createdAt!)}`}
        />
        {withdrawInCrypto ? (
          <Item title="Network:" value={`${network}`} />
        ) : (
          <Item
            title={iban ? 'IBAN:' : 'Reg. No.:'}
            value={iban ?? regNo ?? ''}
          />
        )}
        <Item title="ID:" value={`${finalSwapInvoiceDetails?.swap.id}`} />

        {cryptoTxnId ? (
          <>
            <Item title="Wallet:" value={`${wallet}`} />
            <Item title="Transaction ID:" value={`${cryptoTxnId}`} />
          </>
        ) : (
          <Item
            title={bic ? 'BIC:' : 'Accont No.:'}
            value={bic ?? accountNo ?? ''}
          />
        )}

        <Item
          title="Order ID:"
          value={`${finalSwapInvoiceDetails?.swap.deposit?.bankDepositUniqueId}`}
        />
      </div>

      <div className="py-4 px-5 mt-5 text-xl text-brand-light-text-default font-semi -mx-5">
        Swap Order Receipt
      </div>

      <table className="mt-5">
        <thead>
          <th>Asset</th>
          <th>Description</th>
          <th className="text-right">Quantity</th>
          <th className="text-right">Unit value</th>
          <th className="text-right">Amount</th>
        </thead>
        <tbody className="border-b border-black/10 border-solid">
          <tr>
            <th>{finalSwapInvoiceDetails?.swap.withdraw?.currency.isoCode}</th>
            <th>
              {splitByCapitalLetters(
                finalSwapInvoiceDetails?.swap.withdraw?.currency.currencyType
              )}
            </th>
            <th className="text-right">
              {finalSwapInvoiceDetails?.swap.withdraw &&
                formatNumber(finalSwapInvoiceDetails.swap.withdraw.amount, {
                  currencyType:
                    finalSwapInvoiceDetails.swap.withdraw.currency.currencyType,
                  isoCode: '',
                })}
            </th>
            <th className="text-right">
              {finalSwapInvoiceDetails?.swap.withdraw &&
                formatNumber(
                  finalSwapInvoiceDetails.swap.withdraw.currencyToEurRate,
                  {
                    currencyType: 'FiatCurrency',
                    isoCode: 'EUR',
                  },
                  roundingDigitsWithdrawUnitValue
                )}
            </th>
            <th className="text-right">
              {finalSwapInvoiceDetails?.swap.deposit &&
                formatNumber(withdrawTotal, {
                  currencyType: 'FiatCurrency',
                  isoCode: 'EUR',
                })}
            </th>
          </tr>
        </tbody>
      </table>

      <table className="mt-10">
        <thead>
          <th>Payment unit</th>
          <th>Description</th>
          <th className="text-right">Quantity</th>
          <th className="text-right">Unit value</th>
          <th className="text-right">Amount</th>
        </thead>
        <tbody className="border-b border-black/10 border-solid">
          <tr>
            <th>{finalSwapInvoiceDetails?.swap.deposit?.currency.isoCode}</th>
            <th>
              {splitByCapitalLetters(
                finalSwapInvoiceDetails?.swap.deposit?.currency.currencyType
              )}
            </th>
            <th className="text-right">
              {finalSwapInvoiceDetails?.swap.deposit &&
                formatNumber(finalSwapInvoiceDetails.swap.deposit.amount, {
                  currencyType:
                    finalSwapInvoiceDetails.swap.deposit.currency.currencyType,
                  isoCode: '',
                })}
            </th>
            <th className="text-right">
              {finalSwapInvoiceDetails?.swap.deposit &&
                formatNumber(
                  finalSwapInvoiceDetails.swap.deposit.currencyToEurRate,
                  {
                    currencyType: 'FiatCurrency',
                    isoCode: 'EUR',
                  },
                  roundingDigitsDepositUnitValue
                )}
            </th>
            <th className="text-right font-semi text-brand-light-text-default">
              <div>
                {finalSwapInvoiceDetails?.swap.deposit &&
                  formatNumber(depositTotal, {
                    currencyType: 'FiatCurrency',
                    isoCode: 'EUR',
                  })}
              </div>
              {finalSwapInvoiceDetails?.swap.deposit &&
                finalSwapInvoiceDetails?.swap.deposit?.currency.isoCode !==
                  'EUR' && (
                  <div className="text-[8px] font-medium text-brand-light-text-description">
                    (
                    {formatNumber(
                      finalSwapInvoiceDetails?.swap.deposit?.amount,
                      finalSwapInvoiceDetails.swap.deposit.currency
                    )}
                    )
                  </div>
                )}
            </th>
          </tr>
        </tbody>
      </table>

      <div className="ml-auto w-4/12 py-2 border-b text-[10px] border-black/10 font-semi text-brand-light-text-default border-solid px-2 ">
        <div className="flex justify-between">
          <p>Sub total:</p>{' '}
          <p>
            {finalSwapInvoiceDetails?.swap.deposit &&
              formatNumber(withdrawTotal, {
                currencyType: 'FiatCurrency',
                isoCode: 'EUR',
              })}
          </p>
        </div>
        <div className=" flex justify-between mt-2 gap-10">
          <p>Fee ({Math.round(totalFee * 100 * 100) / 100}%):</p>
          <p>
            {finalSwapInvoiceDetails?.swap.deposit &&
              formatNumber(totalFee * depositTotal, {
                currencyType: 'FiatCurrency',
                isoCode: 'EUR',
              })}
          </p>
        </div>
      </div>

      <div className="absolute left-0 bottom-0 right-0 text-center py-3 border-t border-solid border-black/10 text-[10px]">
        <p>{COMPANY_INVOICE_LEGAL_LINE}</p>
        <p>support@penning.io - www.penning.dk </p>
      </div>
    </div>
  )
}

export default SwapInvoice
